<script setup lang="ts">
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import Select from '@/components/Form/Select.vue';
import { Button, buttonVariants } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/utils/utils';
import { lyricsLanguageToDisplay } from '@/utils/lyricsLanguageDisplay';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentListIcon,
  CloudArrowDownIcon,
} from '@heroicons/vue/24/solid';
import { useAsyncOperationSubscription } from '@/composables/useAsyncOperationSubscription';
import http from '@/services/http';
import { getEcho } from '@/services/echo';
import {
  fetchLiveTrackLyrics,
  type LiveTrackLyricsQueryResult,
  type TrackLyricsPayload,
} from '@/services/liveTrackLyricsApi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, ref, watch } from 'vue';
import { toast } from 'vue-sonner';
import { useAuthStore } from '@/stores/authStore';

export type { TrackLyricsPayload };

const props = defineProps<{
  liveTrackId: number;
}>();

const queryClient = useQueryClient();

const activeAsyncOperationId = ref<string | null>(null);

useAsyncOperationSubscription(activeAsyncOperationId, {
  onFinished: (payload) => {
    activeAsyncOperationId.value = null;
    if (payload.type !== 'lyrics_fetch') {
      return;
    }
    void queryClient.invalidateQueries({ queryKey: ['liveTrackLyrics', props.liveTrackId] });
  },
});

const draftBody = ref('');
const draftSourceReference = ref('');
const draftLyricsCheckStatusId = ref<string | undefined>(undefined);
const showDeleteConfirm = ref(false);
const showFetchJobLogDialog = ref(false);

const authStore = useAuthStore();
const isDeveloper = computed(() => Boolean(authStore.userData?.is_developer));

function parseLyricsCheckStatusId(value: string | undefined): number | null {
  if (value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

const lyricsQuery = useQuery({
  queryKey: computed(() => ['liveTrackLyrics', props.liveTrackId] as const),
  queryFn: () => fetchLiveTrackLyrics(props.liveTrackId),
  enabled: computed(() => Number.isFinite(props.liveTrackId)),
  refetchInterval: (query) => (query.state.data?.fetchPending === true ? 2000 : false),
  refetchOnWindowFocus: true,
});

watch(
  () => lyricsQuery.data.value?.lyrics,
  (lyrics) => {
    if (lyrics === null || lyrics === undefined) {
      draftBody.value = '';
      draftSourceReference.value = '';
      return;
    }
    draftBody.value = lyrics.lyrics_body ?? '';
    draftSourceReference.value = lyrics.source_reference ?? '';
  },
  { immediate: true },
);

/** Trimmed `source_reference`, or `null` when empty. Single source of truth for
 * both the save payload and the fetch request body. */
function trimmedSourceReference(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** Best human-readable error message from an Axios/HTTP error: first surfaces a
 * Laravel 422 field message (so backend validation like `direct_genius_url`
 * reaches the user verbatim), then falls back to `response.data.message`,
 * then the native `Error.message`, and finally the caller's default. */
function extractRequestErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
    ?.response?.data;
  const firstFieldError = data?.errors !== undefined ? Object.values(data.errors)[0]?.[0] : undefined;
  return firstFieldError ?? data?.message ?? (error as { message?: string })?.message ?? fallback;
}

watch(
  () => lyricsQuery.data.value,
  (data) => {
    if (data === undefined) {
      return;
    }
    const id = data.lyricsCheckStatusId;
    draftLyricsCheckStatusId.value = id != null ? String(id) : undefined;
  },
  { immediate: true },
);

watch(
  () => lyricsQuery.data.value?.fetchPending,
  (pending) => {
    if (pending === false) {
      activeAsyncOperationId.value = null;
    }
  },
);

const displayLanguage = computed(() => lyricsLanguageToDisplay(lyricsQuery.data.value?.lyrics?.language));

const displaySourceName = computed(() => {
  const raw = lyricsQuery.data.value?.lyrics?.source_name;
  return typeof raw === 'string' ? raw.trim() : '';
});

const fetchedDisplay = computed(() => {
  const lyrics = lyricsQuery.data.value?.lyrics;
  const title = typeof lyrics?.fetched_title === 'string' ? lyrics.fetched_title.trim() : '';
  const artist = typeof lyrics?.fetched_artist === 'string' ? lyrics.fetched_artist.trim() : '';
  if (title === '' && artist === '') {
    return '';
  }
  if (title !== '' && artist !== '') {
    return `${title} — ${artist}`;
  }
  return title !== '' ? title : artist;
});

/** A `track_lyrics` row exists (may still have empty body until user saves or fetch completes). */
const hasLyricsOnFile = computed(() => lyricsQuery.data.value?.lyrics != null);

/** First query has completed (successfully) at least once — use this to gate any
 * UI block that needs the lyrics row / meta to be known. */
const isInspectorReady = computed(
  () => !lyricsQuery.isPending.value && lyricsQuery.data.value !== undefined,
);

const showLyricsEmptyInfo = computed(() => isInspectorReady.value && !hasLyricsOnFile.value);

const lyricsBodyPlaceholder = computed(() =>
  hasLyricsOnFile.value ? 'Lyrics text' : 'Paste lyrics here, then click Save…',
);

function resolveLyricsBodyForSave(): string | null {
  const draftTrim = draftBody.value.trim();
  const server = lyricsQuery.data.value?.lyrics?.lyrics_body;
  const serverTrim = server?.trim() ?? '';

  if (draftTrim !== serverTrim) {
    return draftTrim === '' ? null : draftBody.value;
  }

  return serverTrim === '' ? null : server ?? null;
}

const saveMutation = useMutation({
  mutationFn: async () => {
    const liveTrackId = props.liveTrackId;
    // `language` and `source_name` are derived from the fetch pipeline and shown
    // read-only in this inspector, so they are intentionally omitted from the
    // save payload — the backend preserves its existing values for those fields.
    const payload: {
      lyrics_body: string | null;
      source_reference: string | null;
      lyrics_check_status_id?: number | null;
    } = {
      lyrics_body: resolveLyricsBodyForSave(),
      source_reference: trimmedSourceReference(draftSourceReference.value),
    };
    if (isStatusDirty.value) {
      payload.lyrics_check_status_id = parseLyricsCheckStatusId(draftLyricsCheckStatusId.value);
    }
    await http.put(`/api/live_track/${liveTrackId}/lyrics`, payload);
  },
  onSuccess: () => {
    toast.success('Lyrics saved');
    void queryClient.invalidateQueries({ queryKey: ['trackMatching'] });
    void queryClient.invalidateQueries({ queryKey: ['search'] });
    void queryClient.invalidateQueries({ queryKey: ['liveTrackLyrics', props.liveTrackId] });
  },
  onError: (error: unknown) => {
    toast.error(extractRequestErrorMessage(error, 'Failed to save lyrics'));
  },
});

const deleteMutation = useMutation({
  mutationFn: async () => {
    const liveTrackId = props.liveTrackId;
    await http.delete(`/api/live_track/${liveTrackId}/lyrics`);
  },
  onSuccess: () => {
    toast.success('Lyrics removed');
    showDeleteConfirm.value = false;
    void queryClient.invalidateQueries({ queryKey: ['trackMatching'] });
    void queryClient.invalidateQueries({ queryKey: ['search'] });
    void queryClient.invalidateQueries({ queryKey: ['liveTrackLyrics', props.liveTrackId] });
    draftBody.value = '';
    draftSourceReference.value = '';
  },
  onError: (error: unknown) => {
    toast.error(extractRequestErrorMessage(error, 'Failed to delete lyrics'));
  },
});

const fetchMutation = useMutation({
  mutationFn: async () => {
    const liveTrackId = props.liveTrackId;
    const directGeniusUrl = trimmedSourceReference(draftSourceReference.value);
    const response = await http.post<{
      message: string;
      meta: { lyrics_fetch_pending: boolean; async_operation_id: string };
    }>(
      `/api/live_track/${liveTrackId}/lyrics/fetch`,
      directGeniusUrl !== null ? { direct_genius_url: directGeniusUrl } : {},
    );
    return response.data;
  },
  onSuccess: (data) => {
    if (getEcho() !== null) {
      activeAsyncOperationId.value = data.meta.async_operation_id;
    }
    // Backend clears the stored lyrics when queuing a fetch, so drop them from
    // the cache immediately — the watcher on `lyricsQuery.data.value?.lyrics`
    // then resets the textarea and source-URL draft to empty while the spinner
    // is still visible, instead of showing stale text until the refetch lands.
    queryClient.setQueryData(
      ['liveTrackLyrics', props.liveTrackId],
      (previous: LiveTrackLyricsQueryResult | undefined) => ({
        lyrics: null,
        fetchPending: data.meta.lyrics_fetch_pending,
        lyricsCheckStatusId: previous?.lyricsCheckStatusId ?? null,
        lyricsCheckStatuses: previous?.lyricsCheckStatuses ?? [],
        lyricsFetchLastJobLog: previous?.lyricsFetchLastJobLog ?? null,
      }),
    );
  },
  onError: (error: unknown) => {
    toast.error(extractRequestErrorMessage(error, 'Failed to queue lyrics fetch'));
  },
});

const isBusy = computed(
  () =>
    lyricsQuery.isPending.value ||
    saveMutation.isPending.value ||
    deleteMutation.isPending.value ||
    fetchMutation.isPending.value,
);

const isStatusDirty = computed(() => {
  const draft = parseLyricsCheckStatusId(draftLyricsCheckStatusId.value);
  const server = lyricsQuery.data.value?.lyricsCheckStatusId ?? null;
  return draft !== server;
});

const isLyricsBodyDirty = computed(() => {
  const draftTrim = draftBody.value.trim();
  const serverTrim = lyricsQuery.data.value?.lyrics?.lyrics_body?.trim() ?? '';
  return draftTrim !== serverTrim;
});

const isSourceReferenceDirty = computed(() => {
  const draftTrim = draftSourceReference.value.trim();
  const serverTrim = lyricsQuery.data.value?.lyrics?.source_reference?.trim() ?? '';
  return draftTrim !== serverTrim;
});

const lyricsCheckStatusSelectOptions = computed(() =>
  (lyricsQuery.data.value?.lyricsCheckStatuses ?? []).map((row) => ({
    value: String(row.id),
    label: row.name,
  })),
);

const canSaveLyrics = computed(
  () =>
    !isBusy.value &&
    (isStatusDirty.value || isLyricsBodyDirty.value || isSourceReferenceDirty.value),
);

const canDeleteLyrics = computed(() => !isBusy.value && lyricsQuery.data.value?.lyrics != null);

const isLyricsFetchAwaitingResult = computed(
  () =>
    fetchMutation.isPending.value ||
    activeAsyncOperationId.value != null ||
    lyricsQuery.data.value?.fetchPending === true,
);

const fetchJobLogText = computed(() => {
  const log = lyricsQuery.data.value?.lyricsFetchLastJobLog;
  if (log != null && log.trim() !== '') {
    return log;
  }
  return 'No fetch job log stored yet. Run a lyrics fetch, then open this again after it finishes.';
});

function isLikelyHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function openSourceReferenceInBrowser(): void {
  const url = draftSourceReference.value.trim();
  if (!isLikelyHttpUrl(url)) {
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

const sourceUrlInputPlaceholder = computed(() =>
  hasLyricsOnFile.value ? 'Genius lyrics page URL' : 'Optional: Genius lyrics URL (…-lyrics), or leave blank to search',
);
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
    <div class="min-h-0 flex-1 overflow-y-auto px-4 py-4">
      <div v-if="lyricsQuery.isPending.value" class="text-muted-foreground text-sm mb-4">Loading…</div>

      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium leading-none" id="inspector-lyrics-check-status-label"
            >Lyrics check status</span
          >
          <Select
            v-model="draftLyricsCheckStatusId"
            class="w-full"
            aria-labelledby="inspector-lyrics-check-status-label"
            :options="lyricsCheckStatusSelectOptions"
            option-label="label"
            option-value="value"
            placeholder="Select status"
            :disabled="isBusy"
          />
        </div>

        <div
          v-if="showLyricsEmptyInfo"
          class="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/25 px-4 py-3"
          data-testid="inspector-lyrics-empty-state"
        >
          <p class="text-sm font-medium text-foreground">No lyrics on file yet</p>
          <p class="mt-1.5 text-sm text-muted-foreground leading-relaxed">
            Use the cloud button and Source URL field below: leave the URL blank to search Genius for this track, or
            paste a Genius lyrics page link (Genius song URLs include <span class="font-mono text-foreground/90">-lyrics</span> in the path) to fetch that page directly. You can also
            paste lyrics in the editor and save without fetching.
          </p>
        </div>

        <div
          v-if="isInspectorReady"
          class="flex gap-2 items-end"
        >
          <Button
            type="button"
            variant="outline"
            size="icon"
            class="size-9 shrink-0"
            data-testid="inspector-lyrics-fetch-button"
            :disabled="isBusy"
            title="Fetch lyrics from configured source"
            aria-label="Fetch lyrics from configured source"
            @click="fetchMutation.mutate()"
          >
            <CloudArrowDownIcon class="h-4 w-4" />
          </Button>
          <div class="min-w-0 flex-1 flex flex-col gap-1.5">
            <label class="text-sm font-medium leading-none" for="inspector-lyrics-source-url">Source URL</label>
            <Input
              id="inspector-lyrics-source-url"
              v-model="draftSourceReference"
              class="h-9 w-full min-w-0 font-mono text-sm"
              data-testid="inspector-lyrics-source-url-input"
              :placeholder="sourceUrlInputPlaceholder"
              :disabled="isBusy"
              autocomplete="off"
              spellcheck="false"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            class="size-9 shrink-0"
            :disabled="isBusy || !isLikelyHttpUrl(draftSourceReference)"
            title="Open in browser"
            aria-label="Open source reference in browser"
            @click="openSourceReferenceInBrowser"
          >
            <ArrowTopRightOnSquareIcon class="h-4 w-4" />
          </Button>
        </div>

        <div v-if="hasLyricsOnFile" class="flex flex-col gap-3">
          <div class="grid gap-3 sm:grid-cols-2">
            <div class="flex flex-col gap-1.5">
              <span class="text-sm font-medium leading-none">Language</span>
              <p class="text-sm text-muted-foreground">{{ displayLanguage !== '' ? displayLanguage : '—' }}</p>
            </div>
            <div class="flex flex-col gap-1.5">
              <span class="text-sm font-medium leading-none">Source name</span>
              <p class="text-sm text-muted-foreground">{{ displaySourceName !== '' ? displaySourceName : '—' }}</p>
            </div>
          </div>
          <div v-if="fetchedDisplay !== ''" class="flex flex-col gap-1.5">
            <span class="text-sm font-medium leading-none">Fetched as</span>
            <p
              class="text-sm text-muted-foreground"
              data-testid="inspector-lyrics-fetched-display"
            >{{ fetchedDisplay }}</p>
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <div class="flex flex-wrap items-center gap-2">
            <label class="text-sm font-medium leading-none" for="inspector-lyrics-body">Lyrics</label>
            <Button
              v-if="isDeveloper"
              type="button"
              variant="outline"
              size="sm"
              class="h-7 gap-1.5 text-xs"
              data-testid="inspector-lyrics-fetch-job-log-button"
              aria-label="View lyrics fetch job log"
              @click="showFetchJobLogDialog = true"
            >
              <ClipboardDocumentListIcon class="h-3.5 w-3.5" aria-hidden="true" />
              Fetch log
            </Button>
          </div>
          <Textarea
            id="inspector-lyrics-body"
            v-model="draftBody"
            class="min-h-[200px] font-mono text-sm"
            :placeholder="lyricsBodyPlaceholder"
            :disabled="isBusy"
          />
        </div>
      </div>
    </div>

    <div class="shrink-0 flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3">
      <div class="flex flex-wrap gap-2">
        <Button type="button" :disabled="!canSaveLyrics" @click="saveMutation.mutate()">Save</Button>
        <button
          type="button"
          :class="
            cn(buttonVariants({ variant: 'destructive' }), !canDeleteLyrics ? 'pointer-events-none opacity-50' : '')
          "
          :disabled="!canDeleteLyrics"
          @click="showDeleteConfirm = true"
        >
          Delete
        </button>
      </div>
      <div
        v-if="isLyricsFetchAwaitingResult"
        data-testid="inspector-lyrics-fetch-loading"
        class="flex shrink-0 items-center text-muted-foreground"
        role="status"
        aria-label="Lyrics fetch in progress"
      >
        <ArrowPathIcon class="h-5 w-5 animate-spin" aria-hidden="true" />
      </div>
    </div>
  </div>

  <Dialog v-model:open="showFetchJobLogDialog">
    <DialogContent class="max-h-[85vh] max-w-2xl gap-0 p-0">
      <DialogHeader class="px-4 pt-4 pb-2">
        <DialogTitle>Lyrics fetch job log</DialogTitle>
        <DialogDescription class="sr-only">Debug output from the last queued Genius lyrics fetch for this track.</DialogDescription>
      </DialogHeader>
      <pre
        class="max-h-[60vh] overflow-auto border-t px-4 py-3 text-left text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap break-words"
        data-testid="inspector-lyrics-fetch-job-log-dialog"
      >{{ fetchJobLogText }}</pre>
    </DialogContent>
  </Dialog>

  <ConfirmDialog
    v-model:open="showDeleteConfirm"
    title="Remove lyrics?"
    message="This removes stored lyrics for this track. You can add them again later."
    confirm-text="Delete"
    variant="destructive"
    cancel-text="Cancel"
    @confirm="deleteMutation.mutate()"
    @cancel="showDeleteConfirm = false"
  />
</template>
